import type { BuyerSession, CheckoutState } from "./state-machine";
import type { BuyerIdentity, SavedCard, WalletSetup } from "./wallet-api";
import { isEmailValid, orderLimitProblem } from "./format";
import type { PayStatus, PayView } from "@libs/checkout-status";
import type { CodeRequest, IdentityView } from "./ports";
import type { PaymentReason } from "./card-island";
import type { CheckoutGoods } from "@libs/checkout-status";

/**
 * The checkout's state and what is read from it. `CoreState` is what the
 * core holds; `CheckoutSnapshot` is what a renderer reads — the same, plus
 * the few answers every renderer would otherwise work out for itself.
 */

export type EmailState = {
  value: string;
  chip: boolean;
  source: "typed" | "lissen";
  /** The chip shows the SDK's masked address while the wallet is checked;
   *  it is not an address anything can be sent to or paid against. */
  masked?: boolean;
};

/** What the checkout is doing right now, if anything: which press is
 *  moving, or `checking` while the buyer is being attested. */
export type Busy =
  | null
  | "complete"
  | "card"
  | "lissen"
  | "express"
  | "checking"
  | "cart";

/** Why a code was asked for, and so what happens once it is entered. */
export type CodeOutcome =
  | { kind: "wallet" }
  | { kind: "pay"; paymentMethodId: string; keep: boolean }
  | { kind: "remember" };

/**
 * What the bank is being asked about, and so what the line above its page
 * says. `payment_again` is the second check on a first payment: the bank
 * challenged the card as it went into the wallet, and now the payment too.
 */
export type BankReason = "card" | PaymentReason;
export type BankStep = { url: string; reason: BankReason };

/** What the page around the checkout can change after it has started.
 *  (Tokens are not among them: they come through the identity port.) */
export type CheckoutInputs = {
  locale: string | null;
};

export type CoreState = {
  checkout: CheckoutState;
  email: EmailState;
  attested: BuyerIdentity | null;
  selected: string | null;
  remember: boolean;
  /** How the post-payment code went: the card joined the wallet, or only
   *  the address could be kept (a wallet already existed on another card). */
  remembered: null | "card" | "email";
  /** Host mode: silent attestation did not happen; the code takes over. */
  hostNeedsCode: boolean;
  setup: WalletSetup | null;
  busy: Busy;
  notice: string | null;
  outcome: CodeOutcome;
  /** The current `busy` is a payment, not a sign-in or a check: the form
   *  gives way to the progress the moment pay is pressed. */
  payingFlag: boolean;
  /** A payment was started in this view (so its status is told to the end). */
  paidHere: boolean;
  /** A bank's 3-D Secure page, drawn in the checkout under its reason. */
  bank: BankStep | null;
  /** Outside host mode: the success screen, once "You're going" was seen. */
  successShown: boolean;
  /** The host's token was refused, or never came. */
  hostFailed: boolean;
  /** What paid, in words, for the success screen and the host: "Visa 4242". */
  methodLabel: string | null;
  identity: IdentityView;
  inputs: CheckoutInputs;
  pay: PayView;
};

/** Fixed for a checkout's life. */
export type CheckoutConfig = {
  sessionId: string;
  slug: string;
  /** The page around the component verified the buyer (`identity: "host"`). */
  hostIdentity: boolean;
  /** Host identity only: the address is the buyer's own sign-in on the page
   *  (`contact: "fixed"`), so there is no changing it here. */
  fixedContact?: boolean;
  /** An identity origin and an engine to attest against: Lissen surfaces. */
  identityAvailable: boolean;
};

/** The config, plus what the core reads off its identity port. */
export type CoreConfig = CheckoutConfig & {
  /** The host answers first (see `IdentityPort.handed`). */
  hostAnswers: boolean;
};

export function initialState(
  identity: IdentityView,
  inputs: CheckoutInputs,
): CoreState {
  return {
    checkout: { kind: "loading" },
    email: { value: "", chip: false, source: "typed" },
    attested: null,
    selected: null,
    remember: false,
    remembered: null,
    hostNeedsCode: false,
    setup: null,
    busy: null,
    notice: null,
    outcome: { kind: "wallet" },
    payingFlag: false,
    paidHere: false,
    bank: null,
    successShown: false,
    hostFailed: false,
    methodLabel: null,
    identity,
    inputs,
    pay: { status: null, phase: null },
  };
}

// ---- reads -------------------------------------------------------------------

export const sessionOf = (s: CoreState): BuyerSession | null =>
  "session" in s.checkout ? s.checkout.session : null;

export const isZeroTotal = (s: CoreState) => sessionOf(s)?.total_minor === 0;

/** Tickets unless the session is here and none of its lines is for an
 *  event: before the session the checkout keeps the words it always had. */
export const goodsOfSession = (
  session: Pick<BuyerSession, "line_items"> | null,
): CheckoutGoods => {
  const lines = session?.line_items;
  return lines?.length && lines.every((line) => line.event === null)
    ? "items"
    : "tickets";
};

export const goodsOf = (s: CoreState): CheckoutGoods =>
  goodsOfSession(sessionOf(s));

/** The cart is outside an event's per-order limits (LIS-924): what to
 *  choose instead, or null. The engine would refuse it, so nothing pays or
 *  completes meanwhile. */
export const limitProblemOf = (s: CoreState) => {
  const session = sessionOf(s);
  return session ? orderLimitProblem(session) : null;
};

/** A code the identity plane is waiting for, in this checkout's own step. */
export const codeRequestOf = (s: CoreState): CodeRequest | null =>
  s.identity.pending?.kind === "token" ? s.identity.pending : null;

/** The host's token is on its way or being tried: nothing else resolves
 *  the buyer meanwhile. */
export const isAwaitingHost = (s: CoreState, config: CoreConfig) =>
  config.hostAnswers && !s.hostFailed;

export const isPaying = (s: CoreState) =>
  s.checkout.kind === "finalizing" || (s.payingFlag && s.busy !== null);

/** Money is moving: a hold's expiry waits for the payment's own outcome. */
export const isMoving = (s: CoreState) =>
  s.busy !== null && s.busy !== "checking";

/** Where the Pay button's status is heading. */
export function payTargetOf(
  s: CoreState,
  config: CheckoutConfig,
): PayStatus | null {
  if (s.checkout.kind === "success") {
    // A zero-total session took no payment: there are no status steps to
    // present, and its ending is the page's at once.
    if (isZeroTotal(s)) return null;
    return config.hostIdentity || s.paidHere ? "done" : null;
  }
  if (s.checkout.kind === "finalizing") return "issuing";
  return s.payingFlag && s.busy !== null ? "processing" : null;
}

/** An address the tickets can go to (never the SDK's masked one). */
export const isEmailPayable = (s: CoreState) =>
  !s.email.masked &&
  (Boolean(s.attested?.email) ||
    (s.email.chip && isEmailValid(s.email.value)) ||
    (isEmailValid(s.email.value) && s.email.source === "typed"));

/** Where the tickets go: the attested buyer's, the session's, or typed. */
export const destinationOf = (s: CoreState) =>
  s.attested?.email ?? sessionOf(s)?.buyer_email ?? s.email.value;

/** Host mode pays only once the engine has attested the buyer; a guest
 *  pays as soon as there is an address. Neither pays a cart outside the
 *  event's limits. */
export const canPayNow = (s: CoreState, config: CheckoutConfig) =>
  (config.hostIdentity ? Boolean(s.attested) : isEmailPayable(s)) &&
  s.busy === null &&
  limitProblemOf(s) === null;

// ---- the renderer's view -------------------------------------------------------

export type CheckoutSnapshot = Readonly<
  Omit<CoreState, "identity" | "inputs"> & {
    session: BuyerSession | null;
    zeroTotal: boolean;
    /** What the cart sells, for the words that name it. */
    goods: CheckoutGoods;
    hostIdentity: boolean;
    identityAvailable: boolean;
    /** An identity client exists (the code step can verify and resend). */
    identityConnected: boolean;
    /** The code the identity plane is waiting for, if any. */
    code: CodeRequest | null;
    cards: Array<SavedCard>;
    /** The host's token is on its way or being tried. */
    awaitingHost: boolean;
    emailPayable: boolean;
    destination: string;
    canPay: boolean;
    /** The cart is outside an event's per-order limits: what to choose
     *  instead, for the one notice. Nothing pays or completes meanwhile. */
    limitProblem: string | null;
  }
>;

export function snapshotOf(
  s: CoreState,
  config: CoreConfig,
): CheckoutSnapshot {
  const { identity, inputs: _inputs, ...rest } = s;
  return {
    ...rest,
    session: sessionOf(s),
    zeroTotal: isZeroTotal(s),
    goods: goodsOf(s),
    hostIdentity: config.hostIdentity,
    identityAvailable: config.identityAvailable,
    identityConnected: identity.connected,
    code: codeRequestOf(s),
    cards: s.attested?.saved_cards ?? [],
    awaitingHost: isAwaitingHost(s, config),
    emailPayable: isEmailPayable(s),
    destination: destinationOf(s),
    canPay: canPayNow(s, config),
    limitProblem: limitProblemOf(s),
  };
}
